import { html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { TeComponent } from './common/te-component';
import { styleMap } from 'lit/directives/style-map';
import { theme } from './common/theme';

import './te-icon';
// TODO: Should use te-tooltip, not te-popover
import './te-popover';
import './button/te-button-squared';
import * as clipboard from 'clipboard-polyfill/text';
const displayMore = 'Expand';
const displayLess = 'Collapse';
/**
 * Copy Code Button Component
 *
 * @slot - Default slot for code example
 */
@customElement('te-copy-code-button')
export class TeCopyCodeButton extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-copy-code-wrapper {
        position: relative;
      }

      .copy-button-wrapper {
        position: absolute;
        top: 0;
        right: 0;
        height: 40px;
        width: 40px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .te-copy-code-content {
        overflow: hidden;
        transition: max-height 400ms ease-in-out 0s;
        max-height: 224px;
      }

      .display-more {
        position: absolute;
        bottom: 0;
        height: 54px;
        cursor: pointer;
        background: transparent
          linear-gradient(
            180deg,
            ${theme.colors.shade0}00 0%,
            ${theme.colors.shade0}e0 34%,
            ${theme.colors.shade0} 100%
          )
          0% 0% no-repeat padding-box;
        opacity: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
      }

      .gradiant {
        position: absolute;
        top: 0;
        height: 40px;
        background: transparent
          linear-gradient(
            180deg,
            ${theme.colors.shade0}00 0%,
            ${theme.colors.shade0}AC 46%,
            ${theme.colors.shade0} 100%
          )
          0% 0% no-repeat padding-box;
        opacity: 1;
        width: 100%;
        transform: matrix(-1, 0, 0, -1, 0, 0);
      }

      .display-more te-text {
        height: 22px;
        background: ${theme.colors.shade0} 0% 0% no-repeat padding-box;
        box-shadow: ${theme.shadows.secondary};
        border-radius: 12px;
        opacity: 1;
        backdrop-filter: blur(30px);
        -webkit-backdrop-filter: blur(30px);
        padding: 0 16px;
        display: flex;
        align-items: center;
      }
    `,
  ];

  @state() anchorEl: Element | undefined;
  @state() isExpandable = false;
  @state() expandText = displayMore;
  getChildNode() {
    const { shadowRoot } = this;
    if (!shadowRoot) return;

    const slot = shadowRoot.querySelector('slot');
    if (!slot) return;

    const childNodes = slot.assignedNodes({ flatten: true });
    if (!childNodes) return;

    const childNode = Array.prototype.filter.call(
      childNodes,
      (node) => node.nodeType == Node.ELEMENT_NODE
    )[0];
    return childNode;
  }
  handleCopyClick() {
    const childNode = this.getChildNode();
    // NOTE: We use a polyfill for clipboard.writeText as navigator.clipboard API is only allowed over HTTPS,
    // also polyfill adds support for non-major browsers and older versions
    // navigator.clipboard.writeText(childNode.innerText);
    clipboard.writeText(childNode.innerText);

    const el = this.shadowRoot?.querySelector('.copy-button-wrapper');
    if (el) {
      this.anchorEl = el;
    }
  }

  handlePopoverClose() {
    this.anchorEl = undefined;
  }

  onSlotChange() {
    const { scrollHeight } = this.getChildNode();
    if (typeof scrollHeight === 'number') {
      const isExpandable = scrollHeight > 224;
      if (isExpandable !== this.isExpandable) {
        this.isExpandable = isExpandable;
      }
    }
  }
  onContainerClick() {
    const isMore = this.expandText === displayMore;
    this.expandText = isMore ? displayLess : displayMore;
    const wrapper = isMore ? null : this.shadowRoot?.querySelector('.te-copy-code-content');
    wrapper?.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }

  getCodeContentStyle() {
    // to check expandable or not
    return this.isExpandable
      ? this.expandText === displayLess
        ? { maxHeight: '384px', overflow: 'auto' }
        : { maxHeight: '164px', overflow: 'hidden' }
      : {};
  }
  render() {
    return html`
      <div class="te-copy-code-wrapper">
        <div class="gradiant"></div>
        <div class="te-copy-code-content" style=${styleMap(this.getCodeContentStyle())}>
          <slot class="codeContent" @slotchange=${this.onSlotChange}></slot>
        </div>
        <div class="copy-button-wrapper">
          <te-button-squared
            icon="copy-code"
            iconSize="16"
            backgroundColor="transparent"
            iconHoverColor=${theme.colors.primary.v0.toString()}
            @click=${this.handleCopyClick}
          ></te-button-squared>
          <te-popover
            .anchorEl="${this.anchorEl}"
            @teClosed=${this.handlePopoverClose}
          ></te-popover>
        </div>
        ${this.isExpandable
          ? html`
              <div class="display-more" @click=${this.isExpandable ? this.onContainerClick : null}>
                <te-text size="12">${this.expandText}</te-text>
              </div>
            `
          : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-copy-code-button': TeCopyCodeButton;
  }
}
